import { z } from 'zod';
import { CurrencyCode, MinorUnits, ZeroEdgeStatusView } from './money';
import { EngineAction, EngineBet, EngineGameMode, EngineTunable } from './engine';
import { GameRoundResult } from './result';
import { BetLimits, GameKind, GameType, Platform, PlayMode, RoundState } from './operator';

/**
 * Player-facing API consumed by the game client. Authenticated with a bearer
 * session token, never with operator credentials.
 */

export const SessionInitRequest = z.object({
  launchToken: z.string().min(16),
});
export type SessionInitRequest = z.infer<typeof SessionInitRequest>;

/**
 * Fun play. No operator, no launch token, no real money: the caller asks for a
 * game and gets a demo session against a play-money balance. The response is
 * the ordinary session payload, so the client renders and plays it with the
 * same code as a real launch.
 */
export const FunSessionRequest = z.object({
  gameCode: z.string().min(1).max(64),
  currency: CurrencyCode.default('USD'),
  language: z.string().min(2).max(5).default('en'),
  platform: Platform.default('DESKTOP'),
});
export type FunSessionRequest = z.infer<typeof FunSessionRequest>;

export const PlayerView = z.object({
  playerId: z.string(),
  displayName: z.string().nullable(),
  currency: CurrencyCode,
  mode: PlayMode,
});
export type PlayerView = z.infer<typeof PlayerView>;

/**
 * What a client is told about a game before it draws anything.
 *
 * There is one shape for every game, because the platform does not know what
 * games there are. What is specific to a game lives in `config` (a paytable,
 * a grid, a multiplier ladder) and in `schema` (what a bet may contain), and
 * both come from the engine's own describe, recorded when it passed its gate.
 * A new game therefore needs no change here.
 */
export const GameConfigView = z.object({
  gameCode: z.string(),
  name: z.string(),
  mathVersion: z.string(),
  rtp: z.number(),
  /** How the round is played, and so which calls the client may make. */
  mode: EngineGameMode,
  /** The effective house edge for this session, or null when not adjustable. */
  houseEdgeBps: z.number().int().nullable(),
  /** JSON Schema for the game's own requests, as published by the engine. */
  schema: z.record(z.string(), z.unknown()).nullable(),
  /** What an operator may change, and within which bounds. */
  tunables: z.array(EngineTunable),
  /** Opaque, game-owned client configuration. */
  config: z.record(z.string(), z.unknown()),
});
export type GameConfigView = z.infer<typeof GameConfigView>;

/** A round that was interrupted; the client replays it before accepting input. */
export const PendingRoundView = z.object({
  roundId: z.uuid(),
  state: RoundState,
  result: GameRoundResult.nullable(),
  totalWin: MinorUnits,
  /** The engine's redacted view of the round, for a client resuming it. */
  roundState: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type PendingRoundView = z.infer<typeof PendingRoundView>;

export const SessionInitResponse = z.object({
  status: z.literal('OK'),
  sessionToken: z.string(),
  sessionId: z.uuid(),
  expiresAt: z.iso.datetime(),
  player: PlayerView,
  game: GameConfigView,
  limits: BetLimits,
  balance: MinorUnits,
  lobbyUrl: z.string().nullable(),
  pendingRound: PendingRoundView.nullable(),
  zeroEdge: ZeroEdgeStatusView.nullable().optional(),
});
export type SessionInitResponse = z.infer<typeof SessionInitResponse>;

/**
 * A bet on any game.
 *
 * `bet` is whatever that game's schema says it is; the platform reads
 * `amount` to debit the wallet and passes the rest through. `clientRoundId`
 * is the client's own idea of the round, so a retry after a dropped response
 * replays the round it already played rather than opening a second one.
 */
export const RoundBetRequest = z.object({
  bet: EngineBet,
  clientRoundId: z.uuid(),
  /** Runs the round at zero edge, against the operator's daily allowance. */
  zeroEdge: z.boolean().default(false),
});
export type RoundBetRequest = z.infer<typeof RoundBetRequest>;

/** A move in a round that is already open. */
export const RoundActionRequest = z.object({
  roundId: z.uuid(),
  action: EngineAction,
});
export type RoundActionRequest = z.infer<typeof RoundActionRequest>;

/**
 * The answer to a bet or a move.
 *
 * One shape for both, because a client that has just doubled down and a
 * client that has just rolled a die want the same three things: where the
 * round is, what it has won so far, and what its balance is now. `state` is
 * the engine's own view of an open round, redacted by the engine itself;
 * `result` appears once the round settles.
 */
export const RoundResponse = z.object({
  status: z.literal('OK'),
  roundId: z.uuid(),
  /** `null` until the round settles. */
  result: GameRoundResult.nullable(),
  /** The open round as the client may see it; `null` once settled. */
  state: z.record(z.string(), z.unknown()).nullable(),
  balance: MinorUnits,
  settled: z.boolean(),
  zeroEdge: ZeroEdgeStatusView.nullable().optional(),
  effectiveHouseEdgeBps: z.number().int().optional(),
});
export type RoundResponse = z.infer<typeof RoundResponse>;

export const StateResponse = z.object({
  status: z.literal('OK'),
  balance: MinorUnits,
  expiresAt: z.iso.datetime(),
  pendingRound: PendingRoundView.nullable(),
  game: GameConfigView.optional(),
  zeroEdge: ZeroEdgeStatusView.nullable().optional(),
});
export type StateResponse = z.infer<typeof StateResponse>;

// ---------------------------------------------------------------------------
// Public catalogue (lobby)
// ---------------------------------------------------------------------------

export const CatalogueGame = z.object({
  gameCode: z.string(),
  name: z.string(),
  type: GameType,
  kind: GameKind,
  rtp: z.number(),
  thumbnailUrl: z.string().nullable(),
  tags: z.array(z.string()),
  isNew: z.boolean(),
  isHot: z.boolean(),
  sortOrder: z.number().int(),
  demoAvailable: z.boolean(),
});
export type CatalogueGame = z.infer<typeof CatalogueGame>;

export const CatalogueResponse = z.object({
  status: z.literal('OK'),
  games: z.array(CatalogueGame),
});
export type CatalogueResponse = z.infer<typeof CatalogueResponse>;

// ---------------------------------------------------------------------------
// Player history (in-session)
// ---------------------------------------------------------------------------

export const HistoryQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  gameCode: z.string().optional(),
});
export type HistoryQuery = z.infer<typeof HistoryQuery>;

export const HistoryRound = z.object({
  roundId: z.uuid(),
  gameCode: z.string(),
  currency: CurrencyCode,
  betAmount: MinorUnits,
  winAmount: MinorUnits,
  result: GameRoundResult.nullable(),
  startedAt: z.iso.datetime(),
  closedAt: z.iso.datetime().nullable(),
});
export type HistoryRound = z.infer<typeof HistoryRound>;

export const HistoryResponse = z.object({
  status: z.literal('OK'),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  rounds: z.array(HistoryRound),
});
export type HistoryResponse = z.infer<typeof HistoryResponse>;

export const AUTH_SCHEME = 'Bearer';
